import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Bug, X, Activity, Wifi, WifiOff, AlertTriangle, ChevronUp, ChevronDown } from 'lucide-react';
import { Badge } from '../../components/ui/badge';

/**
 * Floating Debug Panel - Shows in development mode
 * Displays real-time system status, API call logs, and quick health info
 */
const FloatingDebugPanel = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [apiCalls, setApiCalls] = useState([]);
  const [wsStatus, setWsStatus] = useState('disconnected');
  const [quickHealth, setQuickHealth] = useState(null);
  
  // Check if development mode
  const isDev = process.env.NODE_ENV === 'development' || 
                window.location.hostname === 'localhost' ||
                window.location.hostname.includes('preview');
  
  // Check if super admin
  const isSuperAdmin = user?.role === 'super_admin';
  
  // Determine if panel should be shown
  const shouldShow = isDev || isSuperAdmin;

  // Intercept fetch to log API calls
  useEffect(() => {
    if (!shouldShow) return;
    const originalFetch = window.fetch;
    
    window.fetch = async (...args) => {
      const url = args[0];
      const startTime = Date.now();
      
      try {
        const response = await originalFetch(...args);
        const duration = Date.now() - startTime;
        
        // Only log API calls
        if (typeof url === 'string' && url.includes('/api/')) {
          const method = args[1]?.method || 'GET';
          const path = url.split('/api/')[1]?.split('?')[0] || url;
          
          setApiCalls(prev => [{
            id: Date.now(),
            method,
            path: `/api/${path}`,
            status: response.status,
            duration,
            timestamp: new Date().toLocaleTimeString()
          }, ...prev.slice(0, 49)]); // Keep last 50
        }
        
        return response;
      } catch (error) {
        const duration = Date.now() - startTime;
        
        if (typeof url === 'string' && url.includes('/api/')) {
          const method = args[1]?.method || 'GET';
          const path = url.split('/api/')[1]?.split('?')[0] || url;
          
          setApiCalls(prev => [{
            id: Date.now(),
            method,
            path: `/api/${path}`,
            status: 'ERROR',
            duration,
            timestamp: new Date().toLocaleTimeString(),
            error: error.message
          }, ...prev.slice(0, 49)]);
        }
        
        throw error;
      }
    };
    
    return () => {
      window.fetch = originalFetch;
    };
  }, [shouldShow]);

  // Check WebSocket status
  useEffect(() => {
    if (!shouldShow) return;
    const checkWs = () => {
      // Check for WebSocket connections
      const wsConnections = performance.getEntriesByType?.('resource')
        .filter(r => r.initiatorType === 'websocket');
      
      // Simple heuristic - check if we have any active websocket
      if (window.WebSocket) {
        setWsStatus('available');
      }
    };
    
    checkWs();
    const interval = setInterval(checkWs, 5000);
    return () => clearInterval(interval);
  }, [shouldShow]);

  // Fetch quick health
  useEffect(() => {
    if (!shouldShow) return;
    const fetchHealth = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/debug/health/quick`);
        if (response.ok) {
          const data = await response.json();
          setQuickHealth(data);
        }
      } catch (error) {
        setQuickHealth({ status: 'error' });
      }
    };
    
    fetchHealth();
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, [shouldShow]);

  // Only show in dev mode or for super admin
  if (!shouldShow) return null;

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 left-4 z-50 p-3 bg-purple-600 hover:bg-purple-700 rounded-full shadow-lg transition-all"
        title="Open Debug Panel"
      >
        <Bug className="h-5 w-5 text-white" />
        {quickHealth?.status !== 'healthy' && (
          <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-pulse" />
        )}
      </button>
    );
  }

  return (
    <div className={`fixed bottom-4 left-4 z-50 bg-slate-900 border border-slate-700 rounded-lg shadow-2xl transition-all ${isMinimized ? 'w-80' : 'w-96'}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-slate-700 bg-slate-800 rounded-t-lg">
        <div className="flex items-center gap-2">
          <Bug className="h-4 w-4 text-purple-400" />
          <span className="text-white font-medium text-sm">Debug Panel</span>
          {isDev && <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">DEV</Badge>}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1 hover:bg-slate-700 rounded"
          >
            {isMinimized ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 hover:bg-slate-700 rounded"
          >
            <X className="h-4 w-4 text-slate-400" />
          </button>
        </div>
      </div>

      {/* Quick Status */}
      <div className="p-3 border-b border-slate-700 bg-slate-850">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <Activity className={`h-4 w-4 ${quickHealth?.status === 'healthy' ? 'text-green-400' : 'text-red-400'}`} />
              <span className="text-xs text-slate-400">API</span>
            </div>
            <div className="flex items-center gap-1">
              {wsStatus === 'available' ? (
                <Wifi className="h-4 w-4 text-green-400" />
              ) : (
                <WifiOff className="h-4 w-4 text-red-400" />
              )}
              <span className="text-xs text-slate-400">WS</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">{apiCalls.length} calls</span>
            <button
              onClick={() => setApiCalls([])}
              className="text-xs text-slate-500 hover:text-white"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {!isMinimized && (
        <div className="max-h-80 overflow-y-auto">
          {/* API Call Log */}
          <div className="p-2">
            <h4 className="text-xs font-medium text-slate-400 mb-2 px-1">Recent API Calls</h4>
            {apiCalls.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-4">No API calls yet</p>
            ) : (
              <div className="space-y-1">
                {apiCalls.slice(0, 20).map(call => (
                  <div
                    key={call.id}
                    className="flex items-center justify-between p-2 bg-slate-800 rounded text-xs hover:bg-slate-750"
                  >
                    <div className="flex items-center gap-2 overflow-hidden">
                      <Badge className={`text-xs px-1 py-0 ${
                        call.method === 'GET' ? 'bg-blue-500/20 text-blue-400' :
                        call.method === 'POST' ? 'bg-green-500/20 text-green-400' :
                        call.method === 'PUT' ? 'bg-yellow-500/20 text-yellow-400' :
                        call.method === 'DELETE' ? 'bg-red-500/20 text-red-400' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>
                        {call.method}
                      </Badge>
                      <span className="text-slate-300 truncate" title={call.path}>
                        {call.path.length > 25 ? call.path.slice(0, 25) + '...' : call.path}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`${
                        call.status === 'ERROR' ? 'text-red-400' :
                        call.status >= 400 ? 'text-red-400' :
                        call.status >= 300 ? 'text-yellow-400' :
                        'text-green-400'
                      }`}>
                        {call.status}
                      </span>
                      <span className="text-slate-500">{call.duration}ms</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Links */}
          {isSuperAdmin && (
            <div className="p-2 border-t border-slate-700">
              <a
                href="/debug"
                className="block w-full p-2 text-center text-xs bg-purple-600 hover:bg-purple-700 text-white rounded"
              >
                Open Full Debug Dashboard
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FloatingDebugPanel;
